/**
 * "Take me there" (DESIGN_PROMOTION §4.6; D69): the card's click, or a plan's human step (docs/plans/change-planner.md
 * D13). It opens the browser panel, shows "ZAC is driving" with where it is going, and asks the backend to walk the
 * user's browser there (POST /execution/teleport). The backend holds the browser's lease for the walk only, so the
 * panel is the user's again when the call returns. The outcome's one line is stored by the backend (in the card's
 * conversation, or the plan's when it has one) and returned here: the chat shows it as a message, the plan's page
 * under the step.
 */
import { client } from '@/lib/api';
import { useBrowserStore } from './browserStore';
import { useChatStore } from './chatStore';

/** Where the click came from: the chat's current conversation, or a plan step. */
export type TeleportFrom = { planId: string; stepKey: string } | undefined;

export async function takeMeThere({ entryId, label, from }: { entryId: string; label: string; from?: TeleportFrom }): Promise<string | null> {
  const conversationId = useChatStore.getState().currentConversationId;
  if (from === undefined && !conversationId) return null;
  const browser = useBrowserStore.getState();
  browser.openPanel();
  browser.setAgentControl(true);
  browser.setDrivingStatus(`Taking you to ${label}…`);
  let line: string;
  try {
    const target = from === undefined ? { conversationId: conversationId! } : { planId: from.planId, stepKey: from.stepKey };
    line = (await client.execution.teleport({ entryId, ...target })).line;
  } catch (err) {
    line = `Couldn't take you there: ${err instanceof Error ? err.message : String(err)}`;
  } finally {
    useBrowserStore.getState().setAgentControl(false);
    useBrowserStore.getState().setDrivingStatus(null);
  }
  if (from === undefined) await useChatStore.getState().addAssistantMessage(line);
  return line;
}
